import { motion, AnimatePresence } from "framer-motion";
import type { Easing } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { AnimatedSection, TextReveal, StaggerChildren, CountUp, MagneticButton } from "./animations";
import { skillDomains, publications } from "@/data/siteContent";
import AITransformation from "./AITransformation";
import { useInsights } from "@/hooks/useInsights";
import { Button } from "@/components/ui/button";

// Shared animation config
const easeStandard: Easing = [0.25, 0.4, 0.25, 1];

const operatingNodes = [
{
  title: "DIRECTION",
  description:
  "The position the work takes, and the one it refuses. Set early, defended late."
},
{
  title: "SYSTEMS",
  description:
  "The connective tissue between product, content and generation. Where consistency stops being a discipline and becomes infrastructure."
},
{
  title: "STANDARDS",
  description:
  "What ships, what doesn't, and the editorial line that distinguishes the two. Held the same on day one and day ninety."
},
{
  title: "EXECUTION",
  description:
  "Critical surfaces drawn personally. UI, visual, copy. The standard is held where it is most often lost: in the detail."
},
{
  title: "AI WORKFLOWS",
  description:
  "Generative pipelines architected end to end. Structured input, governed model behaviour, output a team can actually ship."
},
{
  title: "STAKEHOLDERS",
  description:
  "Translating between the boardroom, the engineering org and the people doing the work. Same standard in all three rooms."
}];


const RecentThoughts = () => {
  const { data: insights } = useInsights();
  const [active, setActive] = useState(0);
  const latestThoughts = (insights || []).slice(0, 3);

  const next = useCallback(() => {
    if (latestThoughts.length === 0) return;
    setActive((prev) => (prev + 1) % latestThoughts.length);
  }, [latestThoughts.length]);

  useEffect(() => {
    if (latestThoughts.length <= 1) return;
    const interval = setInterval(next, 5000);
    return () => clearInterval(interval);
  }, [next, latestThoughts.length]);

  if (latestThoughts.length === 0 || active >= latestThoughts.length) {
    if (active >= latestThoughts.length && latestThoughts.length > 0) {
      setActive(0);
    }
    return (
      <AnimatedSection
        delay={0.3}
        className="px-6 md:px-8 py-10 md:py-14 flex flex-col justify-between">
        
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--block-surface-foreground)/0.55)] block mb-8">
            Recent Thoughts
          </span>
          <p className="text-sm text-[hsl(var(--block-surface-foreground)/0.5)]">No thoughts published yet.</p>
        </div>
      </AnimatedSection>);

  }

  const current = latestThoughts[active];

  return (
    <div className="relative overflow-hidden">
      {/* Background image */}
      <AnimatePresence mode="wait">
        <motion.div
          key={current.id}
          className="absolute inset-0 z-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}>
          
          {current.cover_image_url ?
          <img
            src={current.cover_image_url}
            alt=""
            className="w-full h-full object-cover scale-110"
            loading="lazy" /> :


          <div className="w-full h-full bg-gradient-to-br from-accent/20 to-transparent" />
          }
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 px-6 md:px-8 py-10 md:py-14 flex flex-col justify-between h-full min-h-[320px]">
        <div>
          <div className="flex items-center justify-between mb-8">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--block-surface-foreground)/0.55)]">
              Recent Thoughts
            </span>
            <div className="flex items-center gap-1.5">
              {latestThoughts.map((_, i) =>
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`Show thought ${i + 1} of ${latestThoughts.length}`}
                aria-current={i === active}
                className="relative flex items-center justify-center min-h-[44px] min-w-[44px] p-2">
                
                  <span
                  className={`block h-0.5 rounded-full transition-all duration-slow ${
                  i === active ? "w-6 bg-accent" : "w-3 bg-white/20 hover:bg-white/40"}`
                  } />
                
                </button>
              )}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}>
              
              <span className="font-mono text-xs text-accent block mb-3">
                .0{active + 1}
              </span>
              <Link to={`/thinking/${current.slug}`} className="group">
                <h4 className="font-display text-2xl md:text-3xl uppercase tracking-tight text-[hsl(var(--block-surface-foreground))] mb-3 group-hover:text-accent transition-colors duration-medium leading-tight">
                  {current.title}
                </h4>
              </Link>
              {current.excerpt &&
              <p className="text-sm text-[hsl(var(--block-surface-foreground)/0.6)] leading-relaxed line-clamp-2">
                  {current.excerpt}
                </p>
              }
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-between mt-6">
          <MagneticButton strength={0.15}>
            <Link
              to="/thinking"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-accent hover:text-[hsl(var(--block-surface-foreground))] transition-colors duration-medium group min-h-[44px]">
              
              <span>All Thoughts</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-medium" />
            </Link>
          </MagneticButton>

          <div className="flex gap-2">
            <button
              onClick={() =>
              setActive(
                (prev) =>
                (prev - 1 + latestThoughts.length) % latestThoughts.length
              )
              }
              aria-label="Previous thought"
              className="w-10 h-10 rounded-full border border-[hsl(var(--block-surface-border))] flex items-center justify-center text-[hsl(var(--block-surface-foreground)/0.4)] hover:text-[hsl(var(--block-surface-foreground))] hover:border-[hsl(var(--block-surface-border))] transition-colors duration-medium text-xs min-w-[44px] min-h-[44px]">
              
              ←
            </button>
            <button
              onClick={next}
              aria-label="Next thought"
              className="w-10 h-10 rounded-full border border-[hsl(var(--block-surface-border))] flex items-center justify-center text-[hsl(var(--block-surface-foreground)/0.4)] hover:text-[hsl(var(--block-surface-foreground))] hover:border-[hsl(var(--block-surface-border))] transition-colors duration-medium text-xs min-w-[44px] min-h-[44px]">
              
              →
            </button>
          </div>
        </div>
      </div>
    </div>);

};

const Philosophy = () => {
  const [showPublications, setShowPublications] = useState(false);

  return (
    <section
      id="philosophy"
      className="bg-[hsl(var(--block-surface))] text-[hsl(var(--block-surface-foreground))] rounded-xl mx-4 md:mx-6 mt-2 md:mt-4 mb-12 md:mb-20 overflow-hidden scroll-mt-20">
      
      {/* ABOUT Header, masthead grid rhythm */}
      <div className="grid grid-cols-1 md:grid-cols-12 border-b border-[hsl(var(--block-surface-border))]">
        <div className="md:col-span-8 px-6 md:px-8 py-8 md:py-10 border-b md:border-b-0 md:border-r border-[hsl(var(--block-surface-border))]">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--block-surface-foreground)/0.55)] block mb-6">
            About
          </span>
          <TextReveal delay={0.1}>
            <h2 className="font-display text-[clamp(2rem,5.5vw,4.5rem)] leading-[0.95] uppercase tracking-tight text-[hsl(var(--block-surface-foreground))]">
              DIRECTION AND EXECUTION BELONG TOGETHER.
            </h2>
          </TextReveal>
        </div>

        <div className="md:col-span-4 px-6 md:px-8 py-8 md:py-10 flex flex-col justify-between gap-6">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--block-surface-foreground)/0.55)] block">
            In brief
          </span>
          <dl className="space-y-3 font-mono text-[11px] uppercase tracking-[0.08em]">
            {[
              ["Experience", "15 years"],
              ["Mode", "Embedded leadership"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-4 border-b border-[hsl(var(--block-surface-border))] pb-2">
                <dt className="text-[hsl(var(--block-surface-foreground)/0.45)]">{k}</dt>
                <dd className="text-[hsl(var(--block-surface-foreground))] text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

      </div>


      {/* Expertise & Philosophy */}
      <div className="grid grid-cols-1 md:grid-cols-2 border-t border-[hsl(var(--block-surface-border))]">
        <div className="px-6 md:px-8 py-10 md:py-14 border-b md:border-b-0 md:border-r border-[hsl(var(--block-surface-border))]">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--block-surface-foreground)/0.55)] block mb-8">
            Position
          </span>

          <TextReveal delay={0.1}>
            <p className="font-display text-[clamp(1.5rem,3vw,2.5rem)] uppercase tracking-tight leading-[1.05] text-[hsl(var(--block-surface-foreground))]">
              I STAY CLOSE ENOUGH TO THE WORK TO HOLD THE STANDARD.
            </p>
          </TextReveal>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25, duration: 0.6, ease: easeStandard }}
            className="mt-6 text-sm md:text-base text-[hsl(var(--block-surface-foreground)/0.7)] leading-relaxed max-w-lg"
          >
            I work across the decisions that shape a product and the systems
            that carry those decisions into delivery. The role changes with the
            problem. The standard does not.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.35, duration: 0.6, ease: easeStandard }}
            className="mt-8"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent block mb-3">
              Operating principle
            </span>
            <p className="text-sm md:text-base text-[hsl(var(--block-surface-foreground)/0.55)] leading-relaxed max-w-lg">
              Set the direction clearly. Build the conditions for others to
              execute it well. Intervene where judgment matters most.
            </p>
          </motion.div>

        </div>

        <RecentThoughts />
      </div>

      {/* Designing AI-Native Organizations */}
      <div className="border-t border-[hsl(var(--block-surface-border))]">
        <AITransformation />
      </div>

      {/* Practice Surface, hidden for now */}
      {false && (
        <div className="border-t border-[hsl(var(--block-surface-border))]">
          <div className="px-6 md:px-12 py-16 md:py-24">
            <AnimatedSection delay={0.2}>
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-14">
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--block-surface-foreground)/0.55)] block mb-4">
                    Skills
                  </span>
                  <h3 className="font-display text-[clamp(2rem,5vw,4rem)] leading-[0.9] uppercase tracking-tight text-[hsl(var(--block-surface-foreground))]">
                    Expertise
                  </h3>
                </div>
                <p className="text-sm text-[hsl(var(--block-surface-foreground)/0.55)] max-w-md md:text-right leading-relaxed">
                  Four domains I operate across. AI and automation are not
                  separate disciplines; they are embedded throughout each.
                </p>
              </div>
            </AnimatedSection>

            <div className="border border-[hsl(var(--block-surface-border))] rounded-lg overflow-hidden">
              {skillDomains.map((domain, di) => {
                const isSeam = di === 0;
                return (
                  <motion.div
                    key={domain.category}
                    className={`grid grid-cols-1 md:grid-cols-12 ${
                      di > 0 ? "border-t border-[hsl(var(--block-surface-border))]" : ""
                    } ${isSeam ? "bg-accent/[0.03]" : ""}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: di * 0.08, ease: easeStandard }}
                  >
                    <div className="md:col-span-4 px-6 md:px-8 py-8 md:py-10 md:border-r border-[hsl(var(--block-surface-border))] flex flex-col justify-between gap-6 border-b md:border-b-0 border-[hsl(var(--block-surface-border))]">
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          {isSeam && (
                            <span className="font-mono text-[10px] text-accent">
                              ⟐
                            </span>
                          )}
                          <span
                            className={`font-mono text-[10px] uppercase tracking-[0.18em] ${
                              isSeam
                                ? "text-accent"
                                : "text-[hsl(var(--block-surface-foreground)/0.55)]"
                            }`}
                          >
                            {domain.category}
                          </span>
                        </div>
                        <p className="text-sm text-[hsl(var(--block-surface-foreground)/0.7)] leading-relaxed max-w-xs">
                          {domain.note}
                        </p>
                      </div>
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--block-surface-foreground)/0.3)]">
                        0{di + 1} / 0{skillDomains.length}
                      </span>
                    </div>

                    <div className="md:col-span-8">
                      {domain.skills.map((skill, si) => (
                        <motion.div
                          key={skill.name}
                          className={`group flex items-baseline justify-between gap-6 px-6 md:px-8 py-3.5 ${
                            si > 0 ? "border-t border-[hsl(var(--block-surface-border))]" : ""
                          } hover:bg-[hsl(var(--block-surface-foreground)/0.02)] transition-colors duration-medium`}
                          initial={{ opacity: 0, x: -8 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: di * 0.05 + si * 0.03 }}
                        >
                          <div className="flex items-baseline gap-4 min-w-0">
                            <span className="font-mono text-[10px] text-[hsl(var(--block-surface-foreground)/0.3)] w-6 shrink-0">
                              {String(si + 1).padStart(2, "0")}
                            </span>
                            <span className="text-sm md:text-base text-[hsl(var(--block-surface-foreground)/0.85)] group-hover:text-[hsl(var(--block-surface-foreground))] transition-colors duration-medium truncate">
                              {skill.name}
                            </span>
                          </div>
                          <span
                            className={`font-mono text-[10px] uppercase tracking-[0.18em] shrink-0 text-right max-w-[42%] leading-snug ${
                              (skill as { outcome?: string }).outcome
                                ? "text-[hsl(var(--block-surface-foreground)/0.4)]"
                                : skill.depth === "Lead"
                                  ? "text-accent"
                                  : "text-[hsl(var(--block-surface-foreground)/0.45)]"
                            }`}
                          >
                            {(skill as { outcome?: string }).outcome ?? skill.depth}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {/* Publications & Speaking */}
      <div className="border-t border-[hsl(var(--block-surface-border))]">
        <AnimatedSection delay={0.2}>
          <div className="flex items-center justify-between gap-4 px-6 md:px-12 py-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--block-surface-foreground)/0.55)]">
              Publications & Speaking
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-expanded={showPublications}
              aria-controls="publications-list"
              onClick={() => setShowPublications((current) => !current)}
              className="h-8 rounded-none px-0 font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--block-surface-foreground)/0.55)] hover:bg-transparent hover:text-[hsl(var(--block-surface-foreground))]"
            >
              {showPublications ? "Close" : "View 4"}
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-medium ${showPublications ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </Button>
          </div>
        </AnimatedSection>
        <AnimatePresence initial={false}>
          {showPublications && (
            <motion.div
              id="publications-list"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.45, ease: easeStandard }}
              className="overflow-hidden"
            >
              {publications.slice(0, 4).map((item, index) => (
                <motion.div
                  key={item.title}
                  className="flex min-h-[44px] flex-col justify-between overflow-hidden border-t border-[hsl(var(--block-surface-border))] px-6 py-5 transition-colors duration-medium hover:bg-[hsl(var(--block-surface-foreground)/0.05)] sm:flex-row sm:items-center md:px-12 group relative"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.06, ease: easeStandard }}
                >
                  <motion.div
                    className="absolute bottom-0 left-0 top-0 w-1 origin-top bg-accent"
                    initial={{ scaleY: 0 }}
                    whileHover={{ scaleY: 1 }}
                    transition={{ duration: 0.35 }}
                  />

                  <div className="relative z-10 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <span className="w-24 shrink-0 font-mono text-xs uppercase tracking-wider text-[hsl(var(--block-surface-foreground)/0.4)]">
                      {item.type}
                    </span>
                    <motion.span
                      className="font-display text-xl uppercase tracking-tight transition-colors duration-medium group-hover:text-accent md:text-2xl"
                      whileHover={{ x: 10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {item.title}
                    </motion.span>
                  </div>
                  <div className="relative z-10 mt-2 flex items-center gap-4 sm:mt-0">
                    <span className="text-xs text-[hsl(var(--block-surface-foreground)/0.5)]">{item.venue}</span>
                    <span className="font-mono text-xs text-[hsl(var(--block-surface-foreground)/0.4)]">{item.year}</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>);

};

export default Philosophy;